import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  LinearProgress,
  Divider,
} from '@mui/material';
import { Info as InfoIcon } from '@mui/icons-material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { statsApi } from '../../services/api';
import usePageLoadingEffect from '../../hooks/usePageLoadingEffect';

function StatCard({ title, value, total, color = 'primary' }) {
  const percentage = total > 0 ? (value / total) * 100 : 0;

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          {title}
        </Typography>
        <Typography variant="h4" component="div">
          {value?.toLocaleString() ?? '-'}
        </Typography>
        {total && (
          <>
            <Typography variant="body2" color="text.secondary">
              of {total.toLocaleString()} ({percentage.toFixed(1)}%)
            </Typography>
            <LinearProgress
              variant="determinate"
              value={percentage}
              color={color}
              sx={{ mt: 1 }}
            />
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default function PipelineStats() {
  const [stats, setStats] = useState(null);
  const [retailers, setRetailers] = useState([]);
  const [brands, setBrands] = useState([]);
  const [selectedRetailer, setSelectedRetailer] = useState('');
  const [selectedBrand, setSelectedBrand] = useState('');
  const [loading, setLoading] = useState(true);
  usePageLoadingEffect(loading);
  const [error, setError] = useState(null);

  const loadStats = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {};
      if (selectedRetailer) params.retailer_id = selectedRetailer;
      if (selectedBrand) params.normalized_brand = selectedBrand;

      const data = await statsApi.getPipelineStats(params);
      setStats(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedRetailer, selectedBrand]);

  const loadFilters = useCallback(async () => {
    try {
      const [retailersData, brandsData] = await Promise.all([
        statsApi.getRetailers(),
        statsApi.getBrands(selectedRetailer || null),
      ]);
      setRetailers(retailersData);
      setBrands(brandsData);
    } catch (err) {
      console.error('Failed to load filters:', err);
    }
  }, [selectedRetailer]);

  useEffect(() => {
    loadFilters();
  }, [loadFilters]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  if (loading && !stats) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' }, mb: 3, gap: { xs: 1, sm: 2 }, flexWrap: 'wrap' }}>
        <Box sx={{ flexGrow: 1, display: { xs: 'none', sm: 'block' } }} />

        <FormControl size="small" sx={{ minWidth: { xs: 0, sm: 200 } }}>
          <InputLabel>Retailer</InputLabel>
          <Select
            value={selectedRetailer}
            label="Retailer"
            onChange={(e) => {
              setSelectedRetailer(e.target.value);
              setSelectedBrand('');
            }}
          >
            <MenuItem value="">All Retailers</MenuItem>
            {retailers.map((r) => (
              <MenuItem key={r.id} value={r.id}>
                {r.name} ({r.product_count})
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: { xs: 0, sm: 200 } }}>
          <InputLabel>Brand</InputLabel>
          <Select
            value={selectedBrand}
            label="Brand"
            onChange={(e) => setSelectedBrand(e.target.value)}
          >
            <MenuItem value="">All Brands</MenuItem>
            {brands.map((b) => (
              <MenuItem key={b.brand} value={b.brand}>
                {b.brand} ({b.product_count})
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Tooltip title="Refresh">
          <IconButton onClick={loadStats} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {stats?.from_cache && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Showing cached results (updated {Math.round((Date.now() / 1000 - stats.timestamp))} seconds ago)
        </Alert>
      )}

      <Grid container spacing={2}>
        {/* Overview */}
        <Grid size={{ xs: 12, md: 6, lg: 4 }}>
          <StatCard
            title="Active Products"
            value={stats?.active_products}
          />
        </Grid>

        {/* Core Identity */}
        <Grid size={12}>
          <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
            Core Identity
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <StatCard
            title="Has Normalized Brand"
            value={stats?.core_identity?.has_normalized_brand}
            total={stats?.active_products}
            color="success"
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <StatCard
            title="Has Normalized Product ID"
            value={stats?.core_identity?.has_normalized_product_id}
            total={stats?.active_products}
            color="success"
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <StatCard
            title="Has Normalized Product Name"
            value={stats?.core_identity?.has_normalized_product_name}
            total={stats?.active_products}
            color="success"
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <StatCard
            title="Has Normalized Description"
            value={stats?.core_identity?.has_normalized_description}
            total={stats?.active_products}
            color="success"
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <StatCard
            title="Has Image"
            value={stats?.core_identity?.has_image}
            total={stats?.active_products}
            color="success"
          />
        </Grid>

        {/* Color */}
        <Grid size={12}>
          <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
            Color
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <StatCard
            title="Has Raw Color"
            value={stats?.color?.has_raw_color}
            total={stats?.active_products}
            color="info"
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <StatCard
            title="Has Normalized Color"
            value={stats?.color?.has_normalized_color}
            total={stats?.active_products}
            color="info"
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <StatCard
            title="Has Extracted Color(s) from Image"
            value={stats?.color?.has_extracted_colors}
            total={stats?.core_identity?.has_image}
            color="info"
          />
        </Grid>

        {/* Category */}
        <Grid size={12}>
          <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
            Category
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <StatCard
            title="Has Category in Description"
            value={stats?.category?.has_description_category}
            total={stats?.active_products}
            color="secondary"
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <StatCard
            title="Has Extracted Category from Image"
            value={stats?.category?.has_extracted_category}
            total={stats?.core_identity?.has_image}
            color="secondary"
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <StatCard
            title="Has Normalized Category"
            value={stats?.category?.has_normalized_category}
            total={stats?.active_products}
            color="secondary"
          />
        </Grid>

        {/* Pattern */}
        <Grid size={12}>
          <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
            Pattern
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <StatCard
            title="Has Extracted Pattern(s) from Image"
            value={stats?.pattern?.has_extracted_pattern}
            total={stats?.core_identity?.has_image}
            color="info"
          />
        </Grid>

        {/* Eligible For Embedding */}
        <Grid size={12}>
          <Divider sx={{ mt: 2 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, mb: 1 }}>
            <Typography variant="h6">
              Eligible For Embedding
            </Typography>
            <Tooltip
              title={
                <Box sx={{ p: 0.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>
                    Active, non-DEMO products with:
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block' }}>
                    1. Normalized Brand
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block' }}>
                    2. Normalized Product ID
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block' }}>
                    3. Normalized Product Name
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontWeight: 'bold' }}>
                    Category (with fallback):
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block' }}>
                    4. Normalized Category (from extracted_category OR description subcategory)
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontWeight: 'bold' }}>
                    Color (with fallback):
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block' }}>
                    5. Normalized Color, OR AI-extracted colors from image analysis
                  </Typography>
                </Box>
              }
              placement="right"
              arrow
            >
              <InfoIcon sx={{ ml: 1, fontSize: 18, color: 'text.secondary', cursor: 'help' }} />
            </Tooltip>
          </Box>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <StatCard
            title="Eligible Products"
            value={stats?.embedding_eligibility?.eligible}
            total={stats?.active_products}
            color="success"
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <StatCard
            title="Has Embedding"
            value={stats?.embeddings?.has_embedding}
            total={stats?.embedding_eligibility?.eligible}
          />
        </Grid>

        {/* Graph */}
        <Grid size={12}>
          <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
            Graph
          </Typography>
        </Grid>

        {/* Neo4j Stats */}
        {stats?.neo4j?.available && (
          <>
            <Grid size={{ xs: 12, md: 4 }}>
              <StatCard
                title="In Neo4j"
                value={stats?.neo4j?.in_neo4j}
                total={stats?.embeddings?.has_embedding}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <StatCard
                title="Has Identical Match"
                value={stats?.neo4j?.has_identical_match}
                total={stats?.neo4j?.in_neo4j}
                color="success"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <StatCard
                title="Has Variant Match"
                value={stats?.neo4j?.has_variant_match}
                total={stats?.neo4j?.in_neo4j}
                color="info"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <StatCard
                title="No Relationships"
                value={stats?.neo4j?.orphan_products}
                color="warning"
              />
            </Grid>
          </>
        )}
        {stats?.neo4j && !stats?.neo4j?.available && (
          <Grid size={12}>
            <Alert severity="warning">
              Neo4j stats unavailable: {stats?.neo4j?.reason}
            </Alert>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}
